import type { FC } from 'react';
import Button from '@components/Button';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import { ExclamationIcon } from '@heroicons/react/outline';
import { useDeleteUser, useUpdateUser } from '@queries/fetchUsers';
import type { CanonicalUserRecordWithCapabilities } from '@root/helpers/user-management/types';

interface UserResendInviteModalProps {
  user: CanonicalUserRecordWithCapabilities;
  handleClose: () => void;
  onRemoveUser?: () => Promise<void>;
}

const UserResendInviteModal: FC<UserResendInviteModalProps> = ({ user, handleClose, onRemoveUser }) => {
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const isLoading = updateUserMutation.isPending || deleteUserMutation.isPending;

  const handleResendInvite = () => {
    updateUserMutation.mutate(
      { username: user.username, payload: { operation: ApiPatchOperation.UserResendInvite } },
      { onSuccess: handleClose },
    );
  };

  const handleUserRemove = () => {
    deleteUserMutation.mutate(user.username, {
      onSuccess: async () => {
        await onRemoveUser?.();
        handleClose();
      },
    });
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent showCloseButton={false} className="sm:max-w-[600px]" aria-describedby={undefined}>
        <div className="flex w-full items-start gap-4">
          <div className="bg-warning-100 mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationIcon className="text-warning-600 h-6 w-6" aria-hidden="true" />
          </div>
          <div className="flex flex-1 flex-col">
            <div className="">
              <DialogTitle className="text-lg leading-6 font-medium text-gray-900">Update Pending User</DialogTitle>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Would you like to{' '}
                  {user?.capabilitiesManager?.canBeResentInvite && (
                    <>
                      resend the invitation email to <b>{user?.email}</b>
                    </>
                  )}
                  {user?.capabilitiesManager?.canBeResentInvite && user?.capabilitiesManager?.canBeRemoved && (
                    <>
                      <br />
                      or{' '}
                    </>
                  )}
                  {user?.capabilitiesManager?.canBeResentInvite && !user?.capabilitiesManager?.canBeRemoved && '?'}
                  {user?.capabilitiesManager?.canBeRemoved && <>remove this user account?</>}
                </p>
              </div>
            </div>

            <div className="mt-12 flex items-center gap-2">
              {user?.capabilitiesManager?.canBeRemoved && (
                <Button kind="danger" className="h-10" onClick={handleUserRemove} loading={isLoading}>
                  Remove User
                </Button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button className="h-10" onClick={() => handleClose()} loading={isLoading}>
                  Cancel
                </Button>
                {user?.capabilitiesManager?.canBeResentInvite && (
                  <Button kind="primary" className="h-10" onClick={handleResendInvite} loading={isLoading}>
                    Resend Invite
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserResendInviteModal;
